import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { formatINR } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";

export default function SanctionLetter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);

  useEffect(() => { api.get(`/applications/${id}`).then(r => setApp(r.data)); }, [id]);

  if (!app) return <div className="p-10 text-slate-500">Loading…</div>;
  const offer = app.loan_offer || {};
  const sanction = app.sanction || {};
  const v = app.vehicle || {};
  const b = app.borrower || {};

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="sanction-letter-page">
      <div className="max-w-4xl mx-auto no-print flex justify-between items-center mb-4">
        <Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft size={16} className="mr-1" /> Back</Button>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500 hidden md:block">Tip: choose <strong>"Save as PDF"</strong> as the destination in the print dialog</div>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => window.print()} data-testid="print-sanction-btn"><Printer size={16} className="mr-1" /> Download PDF / Print</Button>
        </div>
      </div>

      <div className="sanction-letter">
        <div className="border-b border-slate-300 pb-6 mb-6 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold">Red Fort Capital Finance NBFC</div>
            <h1 className="text-3xl font-semibold mt-1">Sanction Letter</h1>
            <div className="text-sm text-slate-600 mt-2">Issued via CarFin India platform</div>
          </div>
          <div className="text-right text-sm">
            <div><span className="text-slate-500">Letter No: </span><span className="font-mono">{sanction.letter_no || "—"}</span></div>
            <div><span className="text-slate-500">Date: </span><span className="font-mono">{(sanction.generated_at || "").slice(0, 10)}</span></div>
            <div><span className="text-slate-500">Validity: </span><span className="font-mono">{sanction.validity_date || "—"}</span></div>
            <div><span className="text-slate-500">Application: </span><span className="font-mono">{app.application_id}</span></div>
          </div>
        </div>

        <p className="text-sm leading-relaxed mb-5">
          Dear <strong>{b.full_name || "Customer"}</strong>,
          <br /><br />
          We are pleased to inform you that Red Fort Capital Finance NBFC has sanctioned your used car loan, subject to the terms and conditions outlined below and the satisfaction of conditions precedent.
        </p>

        <Section title="Borrower">
          <Two l="Name" v={b.full_name} />
          <Two l="Mobile" v={b.mobile} />
          <Two l="PAN" v={b.pan} />
          <Two l="City" v={`${b.city || ""}, ${b.state || ""}`} />
        </Section>

        <Section title="Vehicle">
          <Two l="Make / Model" v={`${v.make || ""} ${v.model || ""} ${v.variant || ""}`} />
          <Two l="Year / Fuel" v={`${v.year || ""} · ${v.fuel_type || ""}`} />
          <Two l="Registration / VIN" v={`${v.registration_number || ""} · ${v.vin || ""}`} />
          <Two l="Odometer / Owners" v={`${v.km_driven || 0} km · ${v.ownership_count || 1} owner(s)`} />
        </Section>

        <Section title="Sanction Terms">
          <Two l="Approved loan amount" v={formatINR(offer.loan_amount)} />
          <Two l="Rate of interest (p.a.)" v={offer.roi ? `${offer.roi}%` : "—"} />
          <Two l="Tenure" v={`${offer.tenure_months || "—"} months`} />
          <Two l="EMI (reducing balance)" v={formatINR(offer.emi)} />
          <Two l="Processing fee" v={formatINR(offer.processing_fee)} />
          <Two l="Down payment required" v={formatINR(offer.down_payment)} />
          <Two l="Lendable value" v={formatINR(app.valuation?.lendable_value)} />
          <Two l="LTV" v={`${Math.round((app.credit_decision?.ltv || 0) * 1000) / 10}%`} />
        </Section>

        <Section title="Conditions Precedent">
          <ul className="list-disc pl-5 text-sm space-y-1">
            <li>Submission and verification of borrower KYC documents (PAN, Aadhaar, address proof).</li>
            <li>Verification of vehicle RC, insurance, and pollution certificate.</li>
            <li>Hypothecation endorsement in favour of Red Fort Capital Finance NBFC.</li>
            <li>eNACH mandate setup for EMI recovery.</li>
            <li>Down payment confirmation from borrower's bank account.</li>
            {(offer.conditions || []).map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </Section>

        <div className="mt-8 text-sm leading-relaxed">
          This sanction letter is valid for 15 days from the date of issue. Loan approval is subject to Red Fort Capital Finance credit policy, vehicle valuation, document verification, and final underwriting approval. Disbursement is contingent on satisfaction of conditions precedent.
        </div>

        <div className="mt-12 flex justify-between items-end text-sm">
          <div>
            <div className="border-t border-slate-300 pt-2 w-48">Authorised Signatory</div>
            <div className="text-xs text-slate-500 mt-1">{sanction.by_name || "Underwriter"} · Red Fort Capital Finance NBFC</div>
          </div>
          <div className="text-xs text-slate-500 max-w-xs text-right">
            Red Fort Capital Finance NBFC<br />Regd. Office: [Address line] · CIN: [CIN]<br />grievance@carfin.com
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold border-b border-slate-200 pb-1 mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
        {children}
      </div>
    </div>
  );
}

function Two({ l, v }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="text-slate-500">{l}</div>
      <div className="font-mono text-slate-900 text-right">{v || "—"}</div>
    </div>
  );
}
